import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router';
import { Loader2 } from 'lucide-react';
import { Toaster } from '@/components/ui/sonner';
import { Layout } from '@/components/layout/Layout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { Login } from '@/pages/Login';
import { PlexCallback } from '@/pages/PlexCallback';
import { Setup } from '@/pages/Setup';
import { Dashboard } from '@/pages/Dashboard';
import { Map } from '@/pages/Map';
import { StatsActivity, StatsUsers, StatsDevices, StatsBandwidth } from '@/pages/stats';
import { LibraryOverview, LibraryQuality, LibraryStorage, LibraryWatch } from '@/pages/library';
import { Users } from '@/pages/Users';
import { UserDetail } from '@/pages/UserDetail';
import { Rules } from '@/pages/Rules';
import { Violations } from '@/pages/Violations';
import { ViolationDetail } from '@/pages/ViolationDetail';
import { History } from '@/pages/History';
import { Settings } from '@/pages/Settings';
import { Debug } from '@/pages/Debug';
import { NotFound } from '@/pages/NotFound';
import { Maintenance } from '@/pages/Maintenance';
import { useMaintenanceMode } from '@/hooks/useMaintenanceMode';

// Lazy load ApiDocs to avoid loading swagger-ui-react (which uses Node's Buffer) on app startup
const ApiDocs = lazy(() => import('@/pages/ApiDocs').then((m) => ({ default: m.ApiDocs })));

export function App() {
  // Automatically update document title based on current route
  useDocumentTitle();
  const { isInMaintenance } = useMaintenanceMode();

  if (isInMaintenance) {
    return <Maintenance />;
  }

  return (
    <>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/auth/plex-callback" element={<PlexCallback />} />
        <Route path="/setup" element={<Setup />} />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="map" element={<Map />} />

          {/* Stats routes */}
          <Route path="stats" element={<Navigate to="/stats/activity" replace />} />
          <Route path="stats/activity" element={<StatsActivity />} />
          <Route path="stats/library" element={<Navigate to="/library" replace />} />
          <Route path="stats/users" element={<StatsUsers />} />

          {/* Performance routes */}
          <Route path="stats/devices" element={<StatsDevices />} />
          <Route path="stats/bandwidth" element={<StatsBandwidth />} />

          {/* Library routes */}
          <Route path="library" element={<LibraryOverview />} />
          <Route path="library/quality" element={<LibraryQuality />} />
          <Route path="library/storage" element={<LibraryStorage />} />
          <Route path="library/watch" element={<LibraryWatch />} />

          {/* Other routes */}
          <Route path="history/:sessionId?" element={<History />} />
          <Route path="users" element={<Users />} />
          <Route path="users/:id" element={<UserDetail />} />
          <Route path="rules" element={<Rules />} />
          <Route path="violations" element={<Violations />} />
          <Route path="violations/:id" element={<ViolationDetail />} />
          <Route path="settings/*" element={<Settings />} />
          <Route
            path="api-docs"
            element={
              <Suspense
                fallback={
                  <div className="flex h-full items-center justify-center">
                    <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
                  </div>
                }
              >
                <ApiDocs />
              </Suspense>
            }
          />

          {/* Hidden debug page (owner only) */}
          <Route path="debug" element={<Debug />} />

          {/* Legacy redirects */}
          <Route path="analytics" element={<Navigate to="/stats/activity" replace />} />
          <Route path="activity" element={<Navigate to="/stats/activity" replace />} />

          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
      <Toaster />
    </>
  );
}
